import { BarChart2, Bell, Moon, PanelLeft, PanelRight, Search, Settings, Sun } from 'lucide-react'

import { LayoutSelector } from './LayoutSelector'

import { useLayoutStore } from '@/stores/layoutStore'
import { useThemeStore } from '@/stores/themeStore'
import { cn } from '@/utils/cn'

interface TopToolbarProps {
  onSearchOpen: () => void
  onSettingsOpen?: () => void
}

export function TopToolbar({ onSearchOpen, onSettingsOpen }: TopToolbarProps) {
  const { theme, toggleTheme } = useThemeStore()
  const { drawingToolbarOpen, watchlistOpen, toggleDrawingToolbar, toggleWatchlist } =
    useLayoutStore()

  return (
    <header className="flex h-9 shrink-0 items-center border-b border-tv-border bg-tv-bg-secondary px-2">
      {/* Left: drawing toolbar toggle + logo */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={toggleDrawingToolbar}
          title="Toggle drawing toolbar"
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded text-tv-text-secondary transition-colors hover:bg-tv-bg-tertiary hover:text-tv-text-primary',
            drawingToolbarOpen && 'text-tv-blue'
          )}
        >
          <PanelLeft className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-1.5 px-2">
          <BarChart2 className="h-4 w-4 text-tv-blue" />
          <span className="text-sm font-semibold tracking-tight text-tv-text-primary">
            MarketHub<span className="text-tv-blue">Pro</span>
          </span>
        </div>

        <div className="mx-1 h-5 w-px bg-tv-border" />

        {/* TV-style layout selector — always visible row of layout preview buttons */}
        <LayoutSelector />
      </div>

      {/* Center: search */}
      <div className="flex flex-1 items-center justify-center">
        <button
          type="button"
          onClick={onSearchOpen}
          className="flex items-center gap-2 rounded border border-tv-border-light bg-tv-bg-tertiary px-3 py-1 text-xs text-tv-text-secondary transition-colors hover:border-tv-blue hover:text-tv-text-primary"
        >
          <Search className="h-3 w-3" />
          <span>Search symbol…</span>
          <kbd className="ml-1 rounded bg-tv-bg-primary px-1 text-2xs text-tv-text-muted">⌘K</kbd>
        </button>
      </div>

      {/* Right: utilities + watchlist toggle */}
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          title="Alerts (coming soon)"
          className="flex h-7 w-7 items-center justify-center rounded text-tv-text-secondary transition-colors hover:bg-tv-bg-tertiary hover:text-tv-text-primary"
        >
          <Bell className="h-3.5 w-3.5" />
        </button>

        <button
          type="button"
          onClick={toggleTheme}
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          className="flex h-7 w-7 items-center justify-center rounded text-tv-text-secondary transition-colors hover:bg-tv-bg-tertiary hover:text-tv-text-primary"
        >
          {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
        </button>

        <button
          type="button"
          onClick={onSettingsOpen}
          title="Settings"
          className="flex h-7 w-7 items-center justify-center rounded text-tv-text-secondary transition-colors hover:bg-tv-bg-tertiary hover:text-tv-text-primary"
        >
          <Settings className="h-3.5 w-3.5" />
        </button>

        <div className="mx-1 h-5 w-px bg-tv-border" />

        <button
          type="button"
          onClick={toggleWatchlist}
          title="Toggle watchlist"
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded text-tv-text-secondary transition-colors hover:bg-tv-bg-tertiary hover:text-tv-text-primary',
            watchlistOpen && 'text-tv-blue'
          )}
        >
          <PanelRight className="h-4 w-4" />
        </button>
      </div>
    </header>
  )
}
